import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import AdminAPI from "../../apis/AdminAPI";
import TreeAPI from "../../apis/TreeAPI";
import Button from "../Button";
import { ReactComponent as Add } from "../../assets/svg/add.svg";
import Modal from "../Modal";
import clean from "clean-deep";
import ShortMessage from "../ShortMessage";
import { useCourses } from "../../hooks/useCourses";

const Codes = () => {
  const { courses } = useCourses();

  const [codes, setCodes] = useState(undefined);
  const [episodes, setEpisodes] = useState(undefined);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selected, setSelected] = useState({});

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailId, setDetailId] = useState(undefined);

  const [showShortMessage, setShowShortMessage] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const [quantity, setQuantity] = useState(10);

  useEffect(() => {
    const unsubscribe = TreeAPI.subscribeToEpisodes(
      (episode) =>
        setEpisodes((state) => ({ ...state, [episode.id]: episode })),
      (key) => setEpisodes((state) => ({ ...state, [key]: undefined }))
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = AdminAPI.subscribeToCodes(
      quantity,
      (code) => setCodes((state) => ({ ...state, [code.id]: code })),
      (key) => setCodes((state) => ({ ...state, [key]: undefined }))
    );
    return () => unsubscribe();
  }, [quantity]);

  const onCreate = () => {
    if (loading) return;
    setLoading(true);
    AdminAPI.createCode(clean(selected))
      .then((code) =>
        setShowShortMessage({
          state: "SUCCESS",
          message: `Código creado! (${code})`,
          onAccept: () => {
            setSelected({});
            setShowCreateModal(false);
            setShowShortMessage(undefined);
          },
        })
      )
      .catch(() =>
        setShowShortMessage({
          state: "WRONG",
          message: "Ocurrió un error",
          onAccept: () => setSelected({}),
        })
      )
      .finally(() => setLoading(false));
  };

  const codesArray = toArray(clean(codes)).sort(
    (a, b) => b.createdAt - a.createdAt
  );
  const episodesArray = toArray(clean(episodes)).sort(
    (a, b) => a.order - b.order
  );

  const onLoad = () => setQuantity((state) => state + 10);

  const ModalDetail = () => {
    const codeEpisodes = toArray(codes?.[detailId]?.episodes).sort(
      (a, b) => episodes[a.id].order - episodes[b.id].order
    );

    return (
      <ModalStyled>
        <ModalContainer>
          <ModalTitle>{codes?.[detailId]?.id}</ModalTitle>
          <Episodes>
            {courses.map((course) => {
              return (
                <CourseBox>
                  <CourseTitle readOnly={true}>{course.title}</CourseTitle>
                  {episodesArray
                    .filter((episode) => course.episodes.includes(episode.id))
                    .map((episode) => (
                      <Episode
                        key={episode.id}
                        selected={codeEpisodes.some((e) => e.id === episode.id)}
                        readonly
                      >
                        {episode.title}
                      </Episode>
                    ))}
                </CourseBox>
              );
            })}
          </Episodes>
          <Button
            secondary
            text="Cerrar"
            onClick={() => {
              setDetailId(undefined);
              setShowDetailModal(false);
            }}
          />
        </ModalContainer>
      </ModalStyled>
    );
  };

  const ModalCreate = (
    <ModalStyled>
      <ModalContainer>
        <ModalTitle>Selecciona</ModalTitle>
        <Episodes>
          {courses.map((course) => {
            return (
              <CourseBox key={course.id}>
                <CourseTitle
                  onClick={() => {
                    setSelected((state) => {
                      let obj = {};
                      let prev = -1;
                      episodesArray
                        .filter((episode) =>
                          course.episodes.includes(episode.id)
                        )
                        .map((episode) => {
                          if (prev === -1) prev = state[episode.id];
                          obj[episode.id] = prev ? undefined : true;
                        });

                      return {
                        ...state,
                        ...obj,
                      };
                    });
                  }}
                >
                  {course.title}
                </CourseTitle>
                {episodesArray
                  .filter((episode) => course.episodes.includes(episode.id))
                  .map((episode) => (
                    <Episode
                      key={episode.id}
                      selected={selected[episode.id]}
                      onClick={() =>
                        setSelected((state) => ({
                          ...state,
                          [episode.id]: state[episode.id] ? undefined : true,
                        }))
                      }
                    >
                      {episode.title}
                    </Episode>
                  ))}
              </CourseBox>
            );
          })}
        </Episodes>

        <Buttons>
          <Button
            secondary
            text="Cancelar"
            onClick={() => {
              setSelected({});
              setShowCreateModal(false);
            }}
          />
          <Button
            primary
            text="Crear"
            onClick={onCreate}
            loading={loading}
            disabled={
              !Object.keys(selected)
                .map((key) => ({ id: key, selected: selected[key] }))
                .some((item) => item.selected)
            }
          />
        </Buttons>
      </ModalContainer>
    </ModalStyled>
  );

  return (
    <Container>
      {showShortMessage && (
        <ShortMessage
          wrong={showShortMessage?.state === "WRONG"}
          success={showShortMessage?.state === "SUCCESS"}
          message={showShortMessage?.message}
          onAccept={() =>
            showShortMessage?.onAccept && showShortMessage.onAccept()
          }
        />
      )}
      {showDetailModal && <ModalDetail />}
      {showCreateModal && ModalCreate}
      <CodesWrapper>
        <Option
          onClick={() => {
            setShowCreateModal(true);
            setSelected({});
          }}
        >
          <AddIcon as={Add} />
        </Option>
        {codesArray.map((code) => (
          <CodeWrapper
            className="p-4 w-[130px] h-[130px]"
            key={code.id}
            activated={code.user}
            onClick={() => {
              setShowDetailModal(true);
              setDetailId(code.id);
            }}
          >
            <CodeName>{code.id}</CodeName>
            {code.email && <Email>{code.email}</Email>}
          </CodeWrapper>
        ))}
        {codesArray.length === quantity && (
          <Option onClick={onLoad}>
            <Plus>+10</Plus>
          </Option>
        )}
      </CodesWrapper>
    </Container>
  );
};

const toArray = (obj) =>
  Object.keys(obj || {}).map((key) => ({ id: key, ...obj[key] }));


const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Episodes = styled.div`
  display: flex;
  flex-direction: column;
  & > *:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const Episode = styled.div`
  width: 100%;
  font-size: 0.9em;
  padding: 10px;
  box-sizing: border-box;

  border-radius: 10px;
  cursor: pointer;
  ${(props) =>
    props.selected &&
    css`
      background-color: #e1f5fe;
    `}

  ${(props) =>
    props.readonly &&
    css`
      cursor: default;
    `}
`;

const ModalTitle = styled.div`
  font-size: 1.2em;
  font-weight: 600;
`;

const ModalContainer = styled.div`
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  & > *:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const ModalStyled = styled(Modal)`
  max-width: 300px;
  width: 100%;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  & > * {
    margin: 5px 0px;
  }

  @media (max-width: 240px) {
    flex-wrap: wrap;
    flex-direction: column-reverse;
    justify-content: center;
  }
`;

const CodeName = styled.div`
  font-size: 1.2em;
  font-weight: 600;
`;

const CodeWrapper = styled.div`
  margin: 5px;
  border-radius: 10px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  width: 110px;
  min-height: 110px;
  cursor: pointer;

  & > *:not(:last-child) {
    margin-bottom: 5px;
  }

  ${(props) =>
    props.activated &&
    css`
      background-color: #2196f3;
      ${CodeName} {
        color: white;
      }
    `}
`;

const Option = styled.div`
  margin: 5px;
  border-radius: 10px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  width: 110px;
  height: 110px;
  cursor: pointer;
  border: 2px dashed #2196f3;
`;

const Plus = styled.div`
  color: #2196f3;
  font-size: 1.3em;
  font-weight: 600;
`;

const AddIcon = styled.div`
  fill: #2196f3;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
`;

const Email = styled.div`
  font-size: 0.7em;
  color: white;
  word-break: break-word;
  text-align: center;
`;

const CodesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
`;

const CourseTitle = styled.div`
  font-weight: 600;
  ${(props) =>
    !props.readOnly &&
    css`
      cursor: pointer;
    `}
  padding: 10px;

  user-select: none;
`;

const CourseBox = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #cccccc;
  padding: 10px;

  margin: 10px 0px;

  border-radius: 10px;

  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

export default Codes;
